import React, { useState } from 'react'
import ReactDOM from 'react-dom/client'
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { createKlearTrpcClient } from '@klear/ui/lib/create-trpc-client'
import { useApiErrorHandler } from '@klear/ui/hooks/use-api-error-handler'
import { AppGate } from '@klear/ui/components/app-gate'
import { useBuildVersionGate } from '@klear/ui/hooks/use-build-version-gate'
import { getApiUrl, getAppBasePath } from '@klear/runtime-env'
import { ROUTE_ADMIN } from '@klear/constants'
import { adminTraderIdFromPathname } from '@/lib/trader-path'
import { trpc } from '@klear/ui/lib/trpc'
import { ThemeProvider } from '@klear/ui/contexts/theme-context'

import App from '@/App'
import '@klear/ui/styles/globals.css'

import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community'
import isString from 'lodash/isString'

ModuleRegistry.registerModules([AllCommunityModule])

export default function Root() {
  const { isMaintenance, onApiError } = useApiErrorHandler(`${getApiUrl()}/health`)
  const { isStale } = useBuildVersionGate()

  const [queryClient] = useState(
    () =>
      new QueryClient({
        queryCache: new QueryCache({ onError: onApiError }),
        mutationCache: new MutationCache({ onError: onApiError }),
        defaultOptions: {
          queries: {
            retry: false,
            refetchOnWindowFocus: false,
            refetchOnMount: true,
            staleTime: 0
          }
        }
      })
  )

  const [trpcClient] = useState(() =>
    createKlearTrpcClient({
      apiUrl: getApiUrl(),
      fetch: (url: RequestInfo | URL, options?: RequestInit) => {
        const newOptions: RequestInit = {
          ...options,
          credentials: 'include' as RequestCredentials
        }

        const traderId = adminTraderIdFromPathname(window.location.pathname, getAppBasePath(ROUTE_ADMIN))
        if (traderId) {
          const urlString = isString(url) ? url : url.toString()
          const urlObj = new URL(urlString, window.location.origin)
          urlObj.searchParams.set('traderId', traderId)
          return fetch(urlObj.toString(), newOptions)
        }

        return fetch(url, newOptions)
      }
    })
  )

  return (
    <React.StrictMode>
      <ThemeProvider>
        <trpc.Provider client={trpcClient} queryClient={queryClient}>
          <QueryClientProvider client={queryClient}>
            <AppGate isMaintenance={isMaintenance} isStale={isStale}>
              <App />
            </AppGate>
          </QueryClientProvider>
        </trpc.Provider>
      </ThemeProvider>
    </React.StrictMode>
  )
}

declare global {
  interface Window {
    __klearAdminReactRoot?: ReturnType<typeof ReactDOM.createRoot>
  }
}
const rootEl = document.getElementById('root')!
const root = window.__klearAdminReactRoot ?? (window.__klearAdminReactRoot = ReactDOM.createRoot(rootEl))
root.render(<Root />)
